import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sendVerificationEmail } from '../services/email';

type View = 'login' | 'signup' | 'verification' | 'forgot-password' | 'forgot-method' | 'forgot-code' | 'forgot-new-password';

const Login: React.FC = () => {
  const [view, setView] = useState<View>('login');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Signup state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordHint, setPasswordHint] = useState('');
  
  // Password validation
  const [passwordChecks, setPasswordChecks] = useState({
    minLength: false,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
  });
  
  // Verification state
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [codeExpiresAt, setCodeExpiresAt] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes in seconds
  const [pendingUser, setPendingUser] = useState<any>(null);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  
  // Forgot password state
  const [resetMethod, setResetMethod] = useState<'email' | 'phone' | null>(null);
  const [resetIdentifier, setResetIdentifier] = useState('');
  const [resetCode, setResetCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [newPasswordChecks, setNewPasswordChecks] = useState({
    minLength: false,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
  });
  
  const { login, signup, sendVerificationCode, resetPasswordWithCode } = useAuth();
  const navigate = useNavigate();

  // Password validation function
  const validatePassword = (pwd: string) => {
    return {
      minLength: pwd.length >= 10,
      hasUpper: /[A-Z]/.test(pwd),
      hasLower: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(pwd),
    };
  };

  // Update password checks on change
  useEffect(() => {
    if (view === 'signup') {
      setPasswordChecks(validatePassword(signupPassword));
    } else if (view === 'forgot-new-password') {
      setNewPasswordChecks(validatePassword(newPassword));
    }
  }, [signupPassword, newPassword, view]);

  // Countdown timer for verification code
  useEffect(() => {
    if (codeExpiresAt && view === 'verification') {
      const interval = setInterval(() => {
        const remaining = Math.max(0, Math.floor((codeExpiresAt - Date.now()) / 1000));
        setTimeRemaining(remaining);
        if (remaining === 0) {
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [codeExpiresAt, view]);

  // Format phone number
  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  // Handle verification code input
  const handleCodeChange = (index: number, value: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...(setter === setVerificationCode ? verificationCode : resetCode)];
    newCode[index] = value.slice(-1);
    setter(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      nextInput?.focus();
    }
    
    // Auto-submit when all digits entered
    if (index === 5 && value && newCode.every(digit => digit !== '')) {
      const fullCode = newCode.join('');
      if (setter === setVerificationCode) {
        handleVerifyCode(fullCode);
      } else {
        handleVerifyResetCode(fullCode);
      }
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/app');
    } catch (err: any) {
      setError(err.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate all fields
    if (!firstName || !lastName || !signupEmail || !phoneNumber || !signupPassword) {
      setError('All required fields must be filled.');
      return;
    }

    // Check password match
    if (signupPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    // Check password requirements
    const allChecksPass = Object.values(passwordChecks).every(check => check);
    if (!allChecksPass) {
      setError('Password does not meet all requirements.');
      return;
    }

    setLoading(true);

    try {
      // Generate 6-digit verification code
      const code = Math.floor(100000 + Math.random() * 900000).toString();

      // Store pending user data
      const newUser = {
        email: signupEmail,
        firstName,
        lastName,
        phoneNumber: phoneNumber.replace(/\D/g, ''),
        password: signupPassword,
        passwordHint: passwordHint || undefined,
      };
      setPendingUser(newUser);

      // Send verification email via Resend
      await sendVerificationEmail(signupEmail, code, firstName);

      // Set expiration time (10 minutes)
      setCodeExpiresAt(Date.now() + 10 * 60 * 1000);
      setTimeRemaining(600);
      setView('verification');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (code?: string) => {
    const codeToVerify = code || verificationCode.join('');
    if (codeToVerify.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }

    if (!pendingUser) {
      setError('No pending signup found. Please start over.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Verify code with backend
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_URL}/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: pendingUser.email,
          code: codeToVerify,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Invalid verification code');
        setVerificationCode(['', '', '', '', '', '']);
        setLoading(false);
        return;
      }

      // Code verified - create account via AuthContext
      try {
        await signup({
          firstName: pendingUser.firstName,
          lastName: pendingUser.lastName,
          email: pendingUser.email,
          phoneNumber: pendingUser.phoneNumber,
          password: pendingUser.password,
          passwordHint: pendingUser.passwordHint,
        });

        // Mark as verified in localStorage if using mock auth
        const tempUser = localStorage.getItem('tempUser');
        if (tempUser) {
          const parsed = JSON.parse(tempUser);
          parsed.verified = true;
          localStorage.setItem('mockUser', JSON.stringify(parsed));
          localStorage.removeItem('tempUser');
        }

        // Create user object after verification
        const userUid = Date.now().toString();
        const user = {
          uid: userUid,
          email: pendingUser.email,
          firstName: pendingUser.firstName,
          lastName: pendingUser.lastName,
          phoneNumber: pendingUser.phoneNumber,
          subscriptionActive: false,
          verified: true,
          createdAt: new Date(),
        };

        // Store in users array
        const users = JSON.parse(localStorage.getItem('users') || '[]');
        // Check if user already exists
        const existingUserIndex = users.findIndex((u: any) => u.email === user.email);
        if (existingUserIndex !== -1) {
          // Update existing user
          users[existingUserIndex] = { ...users[existingUserIndex], ...user };
        } else {
          // Add new user
          users.push(user);
        }
        localStorage.setItem('users', JSON.stringify(users));

        // ✅ STORE AUTH TOKENS
        localStorage.setItem('authToken', user.uid);
        localStorage.setItem('userEmail', user.email);
        localStorage.setItem('subscriptionActive', 'false');
        localStorage.setItem('loginTimestamp', Date.now().toString());

        console.log('✅ New user auth stored:', user.uid, user.email);

        // Redirect to billing
        navigate('/app/billing');
      } catch (signupErr: any) {
        // If signup fails, still redirect (code was verified)
        console.error('Signup error after verification:', signupErr);
        navigate('/app/billing');
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
      setVerificationCode(['', '', '', '', '', '']);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!pendingUser) {
      setError('No pending signup found.');
      return;
    }

    setError('');
    setResendDisabled(true);

    try {
      // Generate new code
      const code = Math.floor(100000 + Math.random() * 900000).toString();

      // Send new email
      await sendVerificationEmail(pendingUser.email, code, pendingUser.firstName);

      // Set expiration time (10 minutes)
      setCodeExpiresAt(Date.now() + 10 * 60 * 1000);
      setTimeRemaining(600);

      // Start 60 second countdown
      setResendCountdown(60);
      const interval = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      setError(err.message || 'Failed to resend code.');
      setResendDisabled(false);
    }
  };

  const handleForgotPasswordMethod = (method: 'email' | 'phone') => {
    setResetMethod(method);
    setResetIdentifier('');
    setView('forgot-password');
  };

  const handleSendResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetIdentifier) {
      setError('Please enter your email or phone number.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await sendVerificationCode(resetMethod || 'email', resetIdentifier);
      setView('forgot-code');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetCode = async (code?: string) => {
    const codeToVerify = code || resetCode.join('');
    if (codeToVerify.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Code will be verified when resetting password
      setView('forgot-new-password');
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
      setResetCode(['', '', '', '', '', '']);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match.');
      return;
    }

    const allChecksPass = Object.values(newPasswordChecks).every(check => check);
    if (!allChecksPass) {
      setError('Password does not meet all requirements.');
      return;
    }

    setLoading(true);

    try {
      await resetPasswordWithCode(resetIdentifier, resetCode.join(''), newPassword);
      setError('');
      // Show success and redirect
      setTimeout(() => {
        setView('login');
        setError('');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  const allPasswordChecksPass = Object.values(passwordChecks).every(check => check);
  const passwordsMatch = signupPassword === confirmPassword && signupPassword !== '';
  const canSubmitSignup = firstName && lastName && signupEmail && phoneNumber && allPasswordChecksPass && passwordsMatch;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Verification Screen
  if (view === 'verification') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src="/coup_logo.png" 
              alt="COUP Logo" 
              className="logo-auth mx-auto mb-4"
            />
            <h1 className="text-3xl font-display font-bold text-white mb-2">
              Verification Required
            </h1>
            <p className="text-text-secondary mb-2">
              We sent a 6-digit code to:
            </p>
            {pendingUser && (
              <p className="text-copper font-semibold mb-2">
                {pendingUser.email}
              </p>
            )}
            <p className="text-text-muted text-sm">
              Code expires in 10 minutes
            </p>
          </div>

          <div className="card-tactical">
            {error && (
              <div className="bg-alert-red bg-opacity-10 border-2 border-alert-red p-4 mb-6">
                <p className="text-alert-red text-sm">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-text-secondary mb-4 font-mono text-sm text-center">
                Enter 6-digit verification code
              </label>
              <div className="flex gap-2 justify-center">
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    id={`code-${index}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value, setVerificationCode)}
                    className="w-12 h-16 text-center text-2xl font-mono bg-black border-2 border-tactical-frame text-white focus:border-copper focus:outline-none"
                  />
                ))}
              </div>
              {timeRemaining > 0 && (
                <p className="text-text-muted text-sm text-center mt-4 font-mono">
                  Code expires in: {formatTime(timeRemaining)}
                </p>
              )}
              {timeRemaining === 0 && (
                <p className="text-alert-red text-sm text-center mt-4 font-mono">
                  Code expired. Please request a new one.
                </p>
              )}
            </div>

            <button
              onClick={() => handleVerifyCode()}
              disabled={loading || verificationCode.some(d => !d) || timeRemaining === 0}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed mb-4"
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>

            <button
              onClick={handleResendCode}
              disabled={resendDisabled || loading}
              className="btn-secondary w-full disabled:opacity-50"
            >
              {resendDisabled ? `Resend in ${resendCountdown}s` : 'Resend Code'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Forgot Password - Method Selection
  if (view === 'forgot-method') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src="/coup_logo.png" 
              alt="COUP Logo" 
              className="logo-auth mx-auto mb-4"
            />
            <h1 className="text-3xl font-display font-bold text-white mb-2">
              Reset Password
            </h1>
            <p className="text-text-secondary">
              Choose how to receive your reset code:
            </p>
          </div>

          <div className="card-tactical space-y-4">
            <button
              onClick={() => handleForgotPasswordMethod('email')}
              className="btn-primary w-full"
            >
              Send code to email
            </button>
            <button
              onClick={() => handleForgotPasswordMethod('phone')}
              className="btn-primary w-full"
            >
              Send code to phone
            </button>
            <button
              onClick={() => {
                setView('login');
                setError('');
              }}
              className="btn-ghost w-full"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Forgot Password - Identifier Entry
  if (view === 'forgot-password') {
    const mockUser = localStorage.getItem('mockUser');
    const passwordHintText = mockUser ? JSON.parse(mockUser).passwordHint : null;

    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src="/coup_logo.png" 
              alt="COUP Logo" 
              className="logo-auth mx-auto mb-4"
            />
            <h1 className="text-3xl font-display font-bold text-white mb-2">
              Reset Password
            </h1>
            <p className="text-text-secondary">
              Enter your {resetMethod === 'phone' ? 'phone number' : 'email address'}
            </p>
          </div>

          <div className="card-tactical">
            {error && (
              <div className="bg-alert-red bg-opacity-10 border-2 border-alert-red p-4 mb-6">
                <p className="text-alert-red text-sm">{error}</p>
              </div>
            )}

            {passwordHintText && (
              <div className="bg-black border-2 border-tactical-frame p-4 mb-6">
                <p className="text-text-secondary text-sm">
                  <span className="font-semibold">Your password hint:</span> {passwordHintText}
                </p>
              </div>
            )}

            <form onSubmit={handleSendResetCode} className="space-y-6">
              <div>
                <label htmlFor="reset-identifier" className="block text-text-secondary mb-2 font-mono text-sm">
                  {resetMethod === 'phone' ? 'Phone Number' : 'Email Address'}
                </label>
                <input
                  id="reset-identifier"
                  type={resetMethod === 'phone' ? 'tel' : 'email'}
                  required
                  value={resetIdentifier}
                  onChange={(e) => {
                    if (resetMethod === 'phone') {
                      setResetIdentifier(formatPhoneNumber(e.target.value));
                    } else {
                      setResetIdentifier(e.target.value);
                    }
                  }}
                  className="input-tactical"
                  placeholder={resetMethod === 'phone' ? '(XXX) XXX-XXXX' : 'your.email@domain.com'}
                />
              </div>

              <button
                type="submit"
                disabled={loading || !resetIdentifier}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>

              <button
                type="button"
                onClick={() => setView('forgot-method')}
                className="btn-ghost w-full"
              >
                Back
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Forgot Password - Code Entry
  if (view === 'forgot-code') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-display font-bold text-white mb-2">
              Enter Verification Code
            </h1>
            <p className="text-text-secondary">
              Enter the 6-digit code sent to {resetIdentifier}
            </p>
          </div>

          <div className="card-tactical">
            {error && (
              <div className="bg-alert-red bg-opacity-10 border-2 border-alert-red p-4 mb-6">
                <p className="text-alert-red text-sm">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <div className="flex gap-2 justify-center">
                {resetCode.map((digit, index) => (
                  <input
                    key={index}
                    id={`reset-code-${index}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value, setResetCode)}
                    className="w-12 h-16 text-center text-2xl font-mono bg-black border-2 border-tactical-frame text-white focus:border-copper focus:outline-none"
                  />
                ))}
              </div>
            </div>

            <button
              onClick={() => handleVerifyResetCode()}
              disabled={loading || resetCode.some(d => !d)}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed mb-4"
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>

            <button
              onClick={handleSendResetCode}
              disabled={loading}
              className="btn-secondary w-full disabled:opacity-50"
            >
              Resend Code
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Forgot Password - New Password
  if (view === 'forgot-new-password') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-display font-bold text-white mb-2">
              Set New Password
            </h1>
          </div>

          <div className="card-tactical">
            {error && (
              <div className="bg-alert-red bg-opacity-10 border-2 border-alert-red p-4 mb-6">
                <p className="text-alert-red text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <label htmlFor="new-password" className="block text-text-secondary mb-2 font-mono text-sm">
                  New Password
                </label>
                <input
                  id="new-password"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-tactical"
                  placeholder="Enter new password"
                />
                <div className="mt-2 space-y-1">
                  {[
                    { check: newPasswordChecks.minLength, text: 'At least 10 characters' },
                    { check: newPasswordChecks.hasUpper, text: 'Contains uppercase letter' },
                    { check: newPasswordChecks.hasLower, text: 'Contains lowercase letter' },
                    { check: newPasswordChecks.hasNumber, text: 'Contains number' },
                    { check: newPasswordChecks.hasSpecial, text: 'Contains special character' },
                  ].map((item, idx) => (
                    <div key={idx} className={`text-xs font-mono ${item.check ? 'text-scan-green' : 'text-text-muted'}`}>
                      {item.check ? '✓' : '○'} {item.text}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="confirm-new-password" className="block text-text-secondary mb-2 font-mono text-sm">
                  Confirm New Password
                </label>
                <input
                  id="confirm-new-password"
                  type="password"
                  required
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="input-tactical"
                  placeholder="Confirm new password"
                />
                {confirmNewPassword && newPassword !== confirmNewPassword && (
                  <p className="text-alert-red text-xs mt-1 font-mono">Passwords do not match</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !Object.values(newPasswordChecks).every(c => c) || newPassword !== confirmNewPassword}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Signup Form
  if (view === 'signup') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <img 
                src="/COUP_Logo.png" 
                alt="COUP: The Counterscript" 
                className="w-auto object-contain mx-auto"
                style={{ height: '256px', maxHeight: '256px' }}
              />
            </Link>
            <h1 className="text-3xl font-display font-bold text-text-primary mb-2 mt-6">
              Create Account
            </h1>
            <p className="text-text-secondary">
              Deploy your tactical literacy platform
            </p>
          </div>

          <div className="card-tactical">
            {error && (
              <div className="bg-alert-red bg-opacity-10 border-2 border-alert-red p-4 mb-6">
                <p className="text-alert-red text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSignup} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-text-secondary mb-2 font-mono text-sm">
                    First Name <span className="text-alert-red">*</span>
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="input-tactical"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-text-secondary mb-2 font-mono text-sm">
                    Last Name <span className="text-alert-red">*</span>
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="input-tactical"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="signup-email" className="block text-text-secondary mb-2 font-mono text-sm">
                  Email Address <span className="text-alert-red">*</span>
                </label>
                <input
                  id="signup-email"
                  type="email"
                  required
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  className="input-tactical"
                  placeholder="your.email@domain.com"
                />
              </div>

              <div>
                <label htmlFor="phoneNumber" className="block text-text-secondary mb-2 font-mono text-sm">
                  Phone Number <span className="text-alert-red">*</span>
                </label>
                <input
                  id="phoneNumber"
                  type="tel"
                  required
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                  className="input-tactical"
                  placeholder="(XXX) XXX-XXXX"
                  maxLength={14}
                />
              </div>

              <div>
                <label htmlFor="signup-password" className="block text-text-secondary mb-2 font-mono text-sm">
                  Password <span className="text-alert-red">*</span>
                </label>
                <input
                  id="signup-password"
                  type="password"
                  required
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)}
                  className="input-tactical"
                  placeholder="Enter password"
                />
                <div className="mt-2 space-y-1">
                  {[
                    { check: passwordChecks.minLength, text: 'At least 10 characters' },
                    { check: passwordChecks.hasUpper, text: 'Contains uppercase letter' },
                    { check: passwordChecks.hasLower, text: 'Contains lowercase letter' },
                    { check: passwordChecks.hasNumber, text: 'Contains number' },
                    { check: passwordChecks.hasSpecial, text: 'Contains special character' },
                  ].map((item, idx) => (
                    <div key={idx} className={`text-xs font-mono ${item.check ? 'text-scan-green' : 'text-text-muted'}`}>
                      {item.check ? '✓' : '○'} {item.text}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-text-secondary mb-2 font-mono text-sm">
                  Confirm Password <span className="text-alert-red">*</span>
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-tactical"
                  placeholder="Confirm password"
                />
                {confirmPassword && !passwordsMatch && (
                  <p className="text-alert-red text-xs mt-1 font-mono">Passwords do not match</p>
                )}
              </div>

              <div>
                <label htmlFor="passwordHint" className="block text-text-secondary mb-2 font-mono text-sm">
                  Password Hint <span className="text-text-muted">(optional)</span>
                </label>
                <input
                  id="passwordHint"
                  type="text"
                  value={passwordHint}
                  onChange={(e) => setPasswordHint(e.target.value)}
                  className="input-tactical"
                  placeholder="Helpful hint to remember your password"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !canSubmitSignup}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            <div className="section-divider my-6"></div>

            <div className="text-center">
              <p className="text-text-secondary text-sm mb-4">
                Already have an account?
              </p>
              <button
                onClick={() => {
                  setView('login');
                  setError('');
                }}
                className="btn-secondary w-full"
              >
                Log In Instead
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Login Form (default view)
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
              src="/coup_logo.png" 
              alt="COUP: The Counterscript" 
              className="w-auto object-contain mx-auto"
              style={{ height: '256px', maxHeight: '256px' }}
            />
          </Link>
          <h1 className="text-3xl font-display font-bold text-text-primary mb-2 mt-6">
            Data Hub Access
          </h1>
          <p className="text-text-secondary">
            Login to manage your classroom sessions
          </p>
        </div>

        <div className="card-tactical">
          {error && (
            <div className="bg-alert-red bg-opacity-10 border-2 border-alert-red p-4 mb-6">
              <p className="text-alert-red text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-text-secondary mb-2 font-mono text-sm">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-tactical"
                placeholder="your.email@domain.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-text-secondary mb-2 font-mono text-sm">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-tactical"
                placeholder="••••••••"
              />
            </div>

            <div className="text-right">
              <button
                type="button"
                onClick={() => {
                  setView('forgot-method');
                  setError('');
                }}
                className="text-text-secondary hover:text-copper transition-colors text-sm font-mono"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>

          <div className="section-divider my-6"></div>

          <div className="text-center">
            <p className="text-text-secondary text-sm mb-4">
              Don't have an account?
            </p>
            <button
              onClick={() => {
                setView('signup');
                setError('');
              }}
              className="btn-secondary w-full"
            >
              Create Account
            </button>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-text-secondary hover:text-copper transition-colors text-sm font-mono">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
